import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { SOURCE_CONFIG } from '../../constants';
import AiPanel from '../shared/AiPanel';
import type { LeadData } from '../../api/client';

function openWhatsApp(phone: string) {
  const digits = phone.replace(/\D/g, '');
  window.open(`https://wa.me/${digits}`, '_blank');
}
function openEmail(email: string, name: string) {
  window.location.href = `mailto:${email}?subject=Following up — ${encodeURIComponent(name)}`;
}

/* ── CRM Sync modal ── */
function CrmSyncModal({ onClose, onAdd }: { onClose: () => void; onAdd: (lead: Partial<LeadData>) => Promise<void> }) {
  const [phase, setPhase] = useState<'idle' | 'syncing' | 'done'>('idle');
  const [selected, setSelected] = useState<number[]>([]);

  const CRM_RECORDS = [
    { id: 1, name: 'Sanjay Desai',  org: 'RetailMax',   email: 'sanjay@retailmax.com',  phone: '+919876543215', stage: 'Proposal',   crmRef: 'SF-00512' },
    { id: 2, name: 'Kiran Patel',   org: 'Infosys',     email: 'kiran@infosys.com',     phone: '+919876543220', stage: 'Discovery',  crmRef: 'SF-00499' },
    { id: 3, name: 'Meena Agarwal', org: 'HDFC Bank',   email: 'meena@hdfc.com',        phone: '+919876543230', stage: 'Negotiation',crmRef: 'SF-00487' },
  ];

  const toggle = (id: number) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const sync = async () => {
    setPhase('syncing');
    await new Promise(r => setTimeout(r, 1400));
    for (const r of CRM_RECORDS.filter(r => selected.includes(r.id))) {
      await onAdd({
        name: r.name, org: r.org, email: r.email, phone: r.phone,
        ini: r.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2),
        heat: 'cold', intent: 20, lastSeen: 'Just now', firstSeen: 'Today',
        sourceType: 'crm', source: `Salesforce · ${r.stage}`,
        signals: [`Deal stage: ${r.stage}`], sessions: 0, totalTime: '—',
        status: 'new', crmRef: r.crmRef,
      });
    }
    setPhase('done');
  };

  const OVERLAY: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(15,27,45,.6)', zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' };
  const CARD: React.CSSProperties    = { background: 'white', borderRadius: '18px', width: '100%', maxWidth: '480px', boxShadow: '0 32px 80px rgba(0,0,0,.3)', overflow: 'hidden' };
  const HDR: React.CSSProperties     = { background: '#0F1B2D', padding: '20px 24px', display: 'flex', alignItems: 'center', gap: '12px' };

  return (
    <div style={OVERLAY} onClick={onClose}>
      <div style={CARD} onClick={e => e.stopPropagation()}>
        <div style={HDR}>
          <i className="ti ti-building-store" style={{ fontSize: '20px', color: '#C8973A' }} />
          <span style={{ fontSize: '17px', fontWeight: '700', color: 'white', flex: 1 }}>CRM Sync</span>
          <button type="button" onClick={onClose} style={{ background: 'rgba(255,255,255,.15)', border: 'none', borderRadius: '8px', color: 'white', width: '30px', height: '30px', cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        {phase === 'done' ? (
          <div style={{ padding: '40px 24px', textAlign: 'center' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#F0FFF4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <i className="ti ti-circle-check" style={{ fontSize: '28px', color: '#22C55E' }} />
            </div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#0F1B2D', marginBottom: '6px' }}>Sync complete</div>
            <div style={{ fontSize: '13px', color: '#718096', marginBottom: '24px' }}>{selected.length} lead{selected.length !== 1 ? 's' : ''} imported from Salesforce</div>
            <button type="button" onClick={onClose} style={{ padding: '12px 32px', borderRadius: '10px', background: '#0F1B2D', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>Done</button>
          </div>
        ) : (
          <>
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #E2E8F0' }}>
              <div style={{ fontSize: '12px', color: '#718096', marginBottom: '12px' }}>Select Salesforce records to import:</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {CRM_RECORDS.map(r => (
                  <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', borderRadius: '10px', border: `1.5px solid ${selected.includes(r.id) ? '#0078D4' : '#E2E8F0'}`, background: selected.includes(r.id) ? '#EBF8FF' : 'white', cursor: 'pointer', transition: 'all .15s' }}>
                    <input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggle(r.id)} style={{ width: '16px', height: '16px', accentColor: '#0078D4', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#0F1B2D' }}>{r.name}</div>
                      <div style={{ fontSize: '11px', color: '#718096' }}>{r.org} · {r.stage}</div>
                    </div>
                    <span style={{ fontSize: '10px', color: '#0078D4', fontWeight: '600', background: '#EBF8FF', padding: '2px 8px', borderRadius: '20px' }}>{r.crmRef}</span>
                  </label>
                ))}
              </div>
            </div>
            <div style={{ padding: '16px 24px' }}>
              <button type="button" onClick={sync} disabled={selected.length === 0 || phase === 'syncing'}
                style={{ width: '100%', padding: '14px', borderRadius: '12px', background: selected.length === 0 ? '#CBD5E0' : '#0078D4', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '15px', fontWeight: '700', cursor: selected.length === 0 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                {phase === 'syncing'
                  ? <><i className="ti ti-loader-2" style={{ animation: 'spin 1s linear infinite' }} /> Syncing…</>
                  : <><i className="ti ti-building-store" /> Import {selected.length > 0 ? `${selected.length} ` : ''}from Salesforce</>}
              </button>
            </div>
          </>
        )}
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const SOURCE_CHIPS = [
  { value: 'manual',  label: 'Manual' },
  { value: 'landing', label: 'Landing page' },
  { value: 'crm',     label: 'CRM / Salesforce' },
  { value: 'playbook',label: 'Referral' },
];

const BLANK_FORM = { name: '', org: '', email: '', phone: '', sourceType: 'manual', note: '' };

/* ── shared modal primitives ── */
const OVERLAY: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(15,27,45,.6)', zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' };
const CARD: React.CSSProperties    = { background: 'white', borderRadius: '18px', width: '100%', maxWidth: '520px', boxShadow: '0 32px 80px rgba(0,0,0,.3)', overflow: 'hidden' };
const HDR: React.CSSProperties     = { background: '#0F1B2D', padding: '20px 24px', display: 'flex', alignItems: 'center', gap: '12px' };
const BODY: React.CSSProperties    = { padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' };
const LBL: React.CSSProperties     = { display: 'block', fontSize: '13px', fontWeight: '600', color: '#1A202C', marginBottom: '6px' };
const inp = (err: boolean): React.CSSProperties => ({
  width: '100%', padding: '11px 14px', borderRadius: '10px', fontFamily: 'inherit', fontSize: '14px',
  border: `1.5px solid ${err ? '#FC8181' : '#E2E8F0'}`, outline: 'none', boxSizing: 'border-box',
  background: err ? '#FFF5F5' : 'white', color: '#1A202C',
});
const ERR: React.CSSProperties = { fontSize: '11px', color: '#E53E3E', marginTop: '4px' };

function AddLeadModal({ onClose }: { onClose: () => void }) {
  const { addLead, toast } = useApp();
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = (k: keyof typeof BLANK_FORM, v: string) => {
    setForm(prev => ({ ...prev, [k]: v }));
    setErrors(prev => ({ ...prev, [k]: '' }));
  };

  const submit = async () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Full name is required';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email';
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      const ini = form.name.trim().split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
      await addLead({
        name: form.name.trim(), org: form.org.trim(),
        email: form.email.trim(), phone: form.phone.trim(),
        ini, heat: 'cold', intent: 10, lastSeen: 'Just now', firstSeen: 'Today',
        sourceType: form.sourceType,
        source: SOURCE_CONFIG[form.sourceType]?.label || 'Manual',
        signals: form.note.trim() ? [form.note.trim()] : [],
        sessions: 0, totalTime: '—', status: 'new',
      });
      toast('Lead added successfully', 'success');
      onClose();
    } catch {
      toast('Failed to add lead', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={OVERLAY} onClick={onClose}>
      <div style={CARD} onClick={e => e.stopPropagation()}>
        {/* Dark header */}
        <div style={HDR}>
          <i className="ti ti-user-plus" style={{ fontSize: '20px', color: '#C8973A' }} />
          <span style={{ fontSize: '17px', fontWeight: '700', color: 'white', flex: 1 }}>Add a lead</span>
          <button type="button" onClick={onClose} style={{ background: 'rgba(255,255,255,.15)', border: 'none', borderRadius: '8px', color: 'white', width: '30px', height: '30px', cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        {/* Body */}
        <div style={BODY}>
          {/* Row 1 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div>
              <label style={LBL}>Full name *</label>
              <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Priya Sharma" style={inp(!!errors.name)} />
              {errors.name && <div style={ERR}>{errors.name}</div>}
            </div>
            <div>
              <label style={LBL}>Organisation</label>
              <input value={form.org} onChange={e => set('org', e.target.value)} placeholder="e.g. FinCorp India" style={inp(false)} />
            </div>
          </div>

          {/* Row 2 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            <div>
              <label style={LBL}>Email</label>
              <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@company.com" style={inp(!!errors.email)} />
              {errors.email && <div style={ERR}>{errors.email}</div>}
            </div>
            <div>
              <label style={LBL}>Phone</label>
              <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+91 98765 43210" style={inp(false)} />
            </div>
          </div>

          {/* Source chips */}
          <div>
            <label style={LBL}>Source</label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {SOURCE_CHIPS.map(s => {
                const active = form.sourceType === s.value;
                return (
                  <button key={s.value} type="button" onClick={() => set('sourceType', s.value)}
                    style={{ padding: '7px 16px', borderRadius: '999px', border: `1.5px solid ${active ? '#C8973A' : '#E2E8F0'}`, background: 'white', fontFamily: 'inherit', fontSize: '13px', fontWeight: active ? '600' : '400', color: active ? '#C8973A' : '#4A5568', cursor: 'pointer', transition: 'all .15s' }}>
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Note */}
          <div>
            <label style={LBL}>Initial note <span style={{ fontWeight: '400', color: '#A0AEC0' }}>(optional)</span></label>
            <textarea value={form.note} onChange={e => set('note', e.target.value)}
              placeholder="e.g. Met at SaaS conference, interested in enterprise plan"
              rows={3} style={{ ...inp(false), resize: 'vertical', minHeight: '80px' }} />
          </div>
        </div>

        {/* Submit */}
        <div style={{ padding: '0 24px 24px' }}>
          <button type="button" onClick={submit} disabled={saving}
            style={{ width: '100%', padding: '14px', borderRadius: '12px', background: saving ? '#718096' : '#0F1B2D', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '15px', fontWeight: '700', cursor: saving ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            {saving
              ? <><i className="ti ti-loader-2" style={{ animation: 'spin 1s linear infinite' }} /> Saving…</>
              : <><i className="ti ti-user-plus" /> Add to leads</>}
          </button>
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const HEAT_CONFIG = {
  hot:  { color: '#EF4444', bg: '#FEF2F2', label: 'HOT'  },
  warm: { color: '#C8973A', bg: '#FFFBF2', label: 'WARM' },
  cold: { color: '#718096', bg: 'var(--surface)', label: 'COLD' },
};

function LeadCard({ lead, onClick, onAI, onDealRoom }: { lead: LeadData; onClick: () => void; onAI: () => void; onDealRoom: () => void }) {
  const src = SOURCE_CONFIG[lead.sourceType] || SOURCE_CONFIG.manual;
  const heat = HEAT_CONFIG[lead.heat] || HEAT_CONFIG.cold;
  const barOp = lead.intent >= 70 ? 1 : lead.intent >= 40 ? 0.6 : 0.3;
  return (
    <div onClick={onClick} style={{ background: 'white', border: '1.5px solid var(--border)', borderRadius: '12px', padding: '14px', cursor: 'pointer', transition: 'all .15s', marginBottom: '10px' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = '#C8973A'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = ''; }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
        <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: 'var(--navy)', color: 'var(--gold)', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{lead.ini}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '14px', fontWeight: '700', color: 'var(--navy)' }}>{lead.name}</span>
            <span style={{ fontSize: '9px', fontWeight: '700', padding: '2px 7px', borderRadius: '20px', background: heat.bg, color: heat.color }}>{heat.label}</span>
            {lead.status === 'new' && <span style={{ fontSize: '9px', fontWeight: '700', padding: '2px 7px', borderRadius: '20px', background: '#EBF8FF', color: '#1E4E8C' }}>New</span>}
            {lead.aiScore !== undefined && <span style={{ fontSize: '9px', fontWeight: '700', padding: '2px 7px', borderRadius: '20px', background: '#EEF2FF', color: '#6366F1', display: 'flex', alignItems: 'center', gap: '3px' }}><i className="ti ti-sparkles" style={{ fontSize: '9px' }} />AI</span>}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text3)', marginTop: '2px' }}>{lead.org}</div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: '18px', fontWeight: '800', color: 'var(--navy)' }}>{lead.intent}%</div>
          <div style={{ fontSize: '9px', color: 'var(--text3)', fontWeight: '600', textTransform: 'uppercase' }}>intent</div>
        </div>
      </div>
      <div style={{ height: '4px', background: 'var(--border)', borderRadius: '4px', marginBottom: '10px' }}>
        <div style={{ width: `${lead.intent}%`, height: '100%', background: '#C8973A', borderRadius: '4px', opacity: barOp }} />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '10px' }}>
        {lead.signals.slice(0, 3).map((s, i) => <span key={i} style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '20px', background: 'var(--surface)', color: 'var(--text2)', border: '1px solid var(--border)' }}>{s}</span>)}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 9px', borderRadius: '20px', fontSize: '10px', fontWeight: '600', color: src.color, background: src.bg }}>
          <i className={`ti ${src.icon}`} style={{ fontSize: '10px' }} /> {src.label}
        </span>
        <span style={{ fontSize: '10px', color: 'var(--text3)' }}>{lead.source}</span>
        {lead.crmRef && <span style={{ fontSize: '10px', color: '#0078D4', fontWeight: '600' }}>{lead.crmRef}</span>}
      </div>
      <div style={{ display: 'flex', gap: '6px' }} onClick={e => e.stopPropagation()}>
        <button type="button" onClick={() => lead.phone && openWhatsApp(lead.phone)} disabled={!lead.phone} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '7px', border: '1.5px solid var(--border2)', background: 'white', fontFamily: 'inherit', fontSize: '11px', fontWeight: '500', cursor: lead.phone ? 'pointer' : 'not-allowed', color: 'var(--text2)', opacity: lead.phone ? 1 : 0.5 }}>
          <i className="ti ti-brand-whatsapp" style={{ color: '#25D366' }} /> WA
        </button>
        <button type="button" onClick={() => lead.email && openEmail(lead.email, lead.name)} disabled={!lead.email} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '7px', border: '1.5px solid var(--border2)', background: 'white', fontFamily: 'inherit', fontSize: '11px', fontWeight: '500', cursor: lead.email ? 'pointer' : 'not-allowed', color: 'var(--text2)', opacity: lead.email ? 1 : 0.5 }}>
          <i className="ti ti-mail" /> Email
        </button>
        <button type="button" onClick={onAI} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '7px', border: '1.5px solid #E8D5A3', background: '#FDF4E7', fontFamily: 'inherit', fontSize: '11px', fontWeight: '600', cursor: 'pointer', color: '#7A5A1A' }}>
          <i className="ti ti-sparkles" style={{ fontSize: '11px' }} /> AI
        </button>
        <button type="button" onClick={onDealRoom} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '7px', background: '#C8973A', border: 'none', fontFamily: 'inherit', fontSize: '11px', fontWeight: '600', cursor: 'pointer', color: 'white', marginLeft: 'auto' }}>
          <i className="ti ti-building" /> Deal Room
        </button>
      </div>
    </div>
  );
}

function LeadDetail({ lead, onClose, onAI, onDealRoom }: { lead: LeadData; onClose: () => void; onAI: () => void; onDealRoom: () => void }) {
  const heat = HEAT_CONFIG[lead.heat] || HEAT_CONFIG.cold;
  const src = SOURCE_CONFIG[lead.sourceType] || SOURCE_CONFIG.manual;
  return (
    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'white', overflowY: 'auto', zIndex: 10 }}>
      <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '12px', background: 'white', position: 'sticky', top: 0, zIndex: 5 }}>
        <button onClick={onClose} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '7px', border: '1.5px solid var(--border2)', background: 'white', fontFamily: 'inherit', fontSize: '12px', cursor: 'pointer', color: 'var(--text2)' }}>
          <i className="ti ti-arrow-left" /> Back
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--navy)' }}>{lead.name}</div>
          <div style={{ fontSize: '12px', color: 'var(--text3)' }}>{lead.org}</div>
        </div>
        <button onClick={onAI} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '8px', background: 'linear-gradient(135deg, #0F1B2D, #1A2D45)', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>
          <i className="ti ti-sparkles" style={{ color: '#C8973A' }} /> AI Agent
        </button>
        <span style={{ fontSize: '10px', fontWeight: '700', padding: '4px 10px', borderRadius: '20px', background: heat.bg, color: heat.color }}>{heat.label}</span>
      </div>
      <div style={{ padding: '20px 24px' }}>
        <div style={{ background: 'var(--navy)', borderRadius: '12px', padding: '20px', marginBottom: '16px', color: 'white' }}>
          <div style={{ fontSize: '11px', fontWeight: '600', opacity: .6, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '6px' }}>Intent Score</div>
          <div style={{ fontSize: '48px', fontWeight: '900', lineHeight: 1, color: '#C8973A' }}>{lead.intent}%</div>
          {lead.aiScore !== undefined && <div style={{ fontSize: '11px', color: 'rgba(200,151,58,.7)', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}><i className="ti ti-sparkles" />AI scored: {lead.aiScore}%</div>}
          <div style={{ height: '6px', background: 'rgba(255,255,255,.15)', borderRadius: '3px', marginTop: '12px' }}>
            <div style={{ width: `${lead.intent}%`, height: '100%', background: '#C8973A', borderRadius: '3px' }} />
          </div>
        </div>
        {lead.aiSuggestion && (
          <div style={{ background: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: '10px', padding: '12px 14px', marginBottom: '12px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#4338CA', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}><i className="ti ti-sparkles" />AI Insight</div>
            <div style={{ fontSize: '12px', color: '#312E81' }}>{lead.aiSuggestion}</div>
          </div>
        )}
        <div style={{ background: 'white', border: '1.5px solid var(--border)', borderRadius: '12px', padding: '14px', marginBottom: '12px' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--navy)', marginBottom: '10px' }}>Contact</div>
          {[{ icon: 'ti-mail', label: lead.email }, { icon: 'ti-phone', label: lead.phone }, { icon: 'ti-calendar', label: `First seen: ${lead.firstSeen}` }, { icon: 'ti-clock', label: `Last seen: ${lead.lastSeen}` }].map((row, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--text2)', marginBottom: '6px' }}>
              <i className={`ti ${row.icon}`} style={{ fontSize: '13px', color: 'var(--text3)', width: '16px' }} /> {row.label}
            </div>
          ))}
        </div>
        <div style={{ background: 'white', border: '1.5px solid var(--border)', borderRadius: '12px', padding: '14px', marginBottom: '12px' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--navy)', marginBottom: '10px' }}>Engagement signals</div>
          {lead.signals.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '12px', color: 'var(--text2)', marginBottom: '6px' }}>
              <span style={{ color: '#22C55E', fontSize: '14px', flexShrink: 0 }}>✓</span> {s}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button onClick={onAI} style={{ padding: '11px', borderRadius: '9px', background: 'linear-gradient(135deg, #0F1B2D, #1A2D45)', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <i className="ti ti-sparkles" style={{ color: '#C8973A' }} /> Open AI Agent
          </button>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={() => lead.phone && openWhatsApp(lead.phone)} disabled={!lead.phone} style={{ flex: 1, padding: '9px', borderRadius: '9px', border: '1.5px solid #25D366', background: 'white', color: '#25D366', fontFamily: 'inherit', fontSize: '13px', fontWeight: '600', cursor: lead.phone ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', opacity: lead.phone ? 1 : 0.5 }}>
              <i className="ti ti-brand-whatsapp" /> WhatsApp
            </button>
            <button type="button" onClick={() => lead.email && openEmail(lead.email, lead.name)} disabled={!lead.email} style={{ flex: 1, padding: '9px', borderRadius: '9px', border: '1.5px solid var(--border2)', background: 'white', color: 'var(--text2)', fontFamily: 'inherit', fontSize: '13px', fontWeight: '600', cursor: lead.email ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', opacity: lead.email ? 1 : 0.5 }}>
              <i className="ti ti-mail" /> Email
            </button>
            <button type="button" onClick={onDealRoom} style={{ flex: 1, padding: '9px', borderRadius: '9px', background: '#C8973A', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              <i className="ti ti-building" /> Deal Room
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LeadsScreen() {
  const { leads, addLead, go, toast } = useApp();
  const [heatFilter, setHeatFilter] = useState<string>('all');
  const [srcFilter, setSrcFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState('intent');
  const [selectedLead, setSelectedLead] = useState<LeadData | null>(null);
  const [aiLead, setAiLead] = useState<LeadData | null>(null);
  const [showAddLead, setShowAddLead] = useState(false);
  const [showCrmSync, setShowCrmSync] = useState(false);

  const filtered = useMemo(() => {
    let items = [...leads];
    if (heatFilter !== 'all') {
      if (heatFilter === 'new') items = items.filter(l => l.status === 'new');
      else items = items.filter(l => l.heat === heatFilter);
    }
    if (srcFilter !== 'all') items = items.filter(l => l.sourceType === srcFilter);
    if (sortBy === 'intent') items.sort((a, b) => b.intent - a.intent);
    else if (sortBy === 'views') items.sort((a, b) => b.sessions - a.sessions);
    return items;
  }, [leads, heatFilter, srcFilter, sortBy]);

  const counts = { hot: leads.filter(l => l.heat === 'hot').length, warm: leads.filter(l => l.heat === 'warm').length, cold: leads.filter(l => l.heat === 'cold').length };

  const chipStyle = (active: boolean): React.CSSProperties => ({
    padding: '5px 12px', borderRadius: '20px', border: '1.5px solid', cursor: 'pointer', fontFamily: 'inherit', fontSize: '11px', fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: '4px',
    background: active ? 'var(--navy)' : 'white', borderColor: active ? 'var(--navy)' : 'var(--border2)', color: active ? 'white' : 'var(--text2)',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '18px 24px 14px', borderBottom: '1px solid var(--border)', flexShrink: 0, background: 'white' }}>
        <div style={{ width: '38px', height: '38px', borderRadius: '11px', background: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className="ti ti-user-bolt" style={{ fontSize: '20px', color: '#D97706' }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '20px', fontWeight: '800', color: 'var(--navy)' }}>Leads</div>
          <div style={{ fontSize: '12px', color: 'var(--text3)', marginTop: '1px' }}>Ranked by intent · AI-powered scoring</div>
        </div>
        <div style={{ display: 'flex', gap: '16px', flexShrink: 0 }}>
          {[{ icon: 'ti-flame', color: '#C8973A', count: counts.hot, label: 'Hot' }, { icon: 'ti-sun', color: 'var(--text2)', count: counts.warm, label: 'Warm' }, { icon: 'ti-snowflake', color: 'var(--text3)', count: counts.cold, label: 'Cold' }].map(s => (
            <div key={s.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '22px', fontWeight: '800', color: 'var(--navy)' }}>{s.count}</div>
              <div style={{ fontSize: '10px', color: 'var(--text3)' }}><i className={`ti ${s.icon}`} style={{ fontSize: '10px', color: s.color }} /> {s.label}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 24px', borderBottom: '1px solid var(--border)', background: 'var(--surface)', flexShrink: 0, flexWrap: 'wrap' }}>
        {(['all', 'hot', 'warm', 'cold', 'new'] as const).map(f => (
          <button key={f} onClick={() => setHeatFilter(f)} style={chipStyle(heatFilter === f)}>{f === 'all' ? `All (${leads.length})` : f.charAt(0).toUpperCase() + f.slice(1)}</button>
        ))}
        <div style={{ width: '1px', height: '18px', background: 'var(--border)', flexShrink: 0 }} />
        {(['all', 'playbook', 'landing', 'crm', 'manual'] as const).map(f => (
          <button key={f} onClick={() => setSrcFilter(f)} style={{ ...chipStyle(srcFilter === f), fontSize: '10px' }}>
            {f !== 'all' && <i className={`ti ${SOURCE_CONFIG[f]?.icon}`} style={{ fontSize: '9px' }} />}
            {f === 'all' ? 'All sources' : SOURCE_CONFIG[f]?.label}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ padding: '6px 10px', borderRadius: '7px', border: '1.5px solid var(--border2)', fontFamily: 'inherit', fontSize: '12px', background: 'white', cursor: 'pointer' }}>
            <option value="intent">By intent</option>
            <option value="recent">Most recent</option>
            <option value="views">Most views</option>
          </select>
          <button type="button" onClick={() => setShowCrmSync(true)} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '7px', background: 'white', border: '1.5px solid #0078D4', color: '#0078D4', fontFamily: 'inherit', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>
            <i className="ti ti-building-store" /> CRM Sync
          </button>
          <button onClick={() => setShowAddLead(true)} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 14px', borderRadius: '7px', background: 'var(--navy)', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>
            <i className="ti ti-plus" /> Add Lead
          </button>
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        <div style={{ height: '100%', overflowY: 'auto', padding: '20px 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px,1fr))', gap: '0 16px' }}>
            {filtered.map(lead => (
              <LeadCard key={lead._id} lead={lead} onClick={() => setSelectedLead(lead)} onAI={() => setAiLead(lead)} onDealRoom={() => go('dealrooms')} />
            ))}
          </div>
        </div>
        {selectedLead && <LeadDetail lead={selectedLead} onClose={() => setSelectedLead(null)} onAI={() => { setAiLead(selectedLead); setSelectedLead(null); }} onDealRoom={() => { setSelectedLead(null); go('dealrooms'); }} />}
      </div>
      {aiLead && <AiPanel lead={aiLead} onClose={() => setAiLead(null)} />}
      {showAddLead && <AddLeadModal onClose={() => setShowAddLead(false)} />}
      {showCrmSync && <CrmSyncModal onClose={() => setShowCrmSync(false)} onAdd={addLead} />}
    </div>
  );
}
